import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";

import {
  coerceColor,
  colorToLegacyString,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";

import { aciToHex } from "../scene/aciColorMap";
import { useT } from "../i18n";
import { useCeColor, useCeColorStore } from "../state/cecolorStore";
import { useEditorStore } from "../state/editorStore";
import { AciColorPicker } from "./AciColorPicker";

/**
 * CECOLOR ribbon swatch — AutoCAD's "Object Color" control in the ribbon
 * Properties panel. Shows the CURRENT colour (the colour newly-created entities
 * inherit); clicking opens the "Select Color" dialog (AciColorPicker). A
 * selection routes through the invertible `header/set-cecolor` command (Command-
 * Engine one-truth, undoable); `editorStore.dispatch` mirrors `doc.cecolor`
 * into the live `cecolorStore` so the create-dispatch boundary stamps new
 * entities (one truth — no manual `setCeColor` here).
 *
 * The swatch fill resolves the union to a CSS colour:
 *   - index   → the ACI palette hex (`aciToHex`)
 *   - rgb     → `#rrggbb`
 *   - byLayer / byBlock / book → a neutral "by-reference" chip (no fixed RGB;
 *     AutoCAD shows the ByLayer/ByBlock label, not a colour).
 *
 * The "Select Color" popup is rendered through a PORTAL to `document.body` and
 * positioned `fixed` from the swatch button's `getBoundingClientRect()`. The
 * swatch is mounted deep inside `.ribbon-shell { overflow: hidden }` (styles.css);
 * an absolutely-positioned popup (even correctly anchored under the button) would
 * be CLIPPED at the short ribbon panel's bottom edge, cutting off most of the
 * 256-swatch Index grid / True Color tab. The body portal escapes every
 * `overflow:hidden` ancestor; the fixed anchor (viewport coords) keeps the popup
 * under the button. A vertical flip opens the popup UPWARD when it would overflow
 * the bottom of the viewport (AutoCAD always shows the picker fully visible).
 */

/** Resolve the swatch fill colour, or `null` for the by-reference cases. */
function resolveSwatchFill(color: EntityColor): string | null {
  switch (color.kind) {
    case "index":
      return aciToHex(color.aci);
    case "rgb":
      return `#${[color.r, color.g, color.b]
        .map((n) => (n & 0xff).toString(16).padStart(2, "0"))
        .join("")}`;
    case "byLayer":
    case "byBlock":
    case "bookColor":
      return null;
  }
}

/** Short bilingual label for the current colour (shown beside the swatch). */
function useColorLabel(color: EntityColor): string {
  const t = useT();
  switch (color.kind) {
    case "byLayer":
      return t("cecolor.swatch.bylayer");
    case "byBlock":
      return t("cecolor.swatch.byblock");
    case "index":
      return String(color.aci);
    case "rgb":
      return `${color.r},${color.g},${color.b}`;
    case "bookColor":
      return color.name;
  }
}

/** Viewport-coordinate anchor for the body-portal'd picker (fixed positioning). */
type PickerAnchor = { left: number; top: number };

export function CeColorSwatch(): ReactElement {
  const t = useT();
  const ceColor = useCeColor((s) => s.ceColor);
  // CECOLOR round-8 FINDING B — subscribe to the document-reset epoch so the
  // LOCAL picker open-state can be closed on reset / new-drawing. The picker's
  // open-state lives in this component (below); resetShellState (which closes the
  // command-line COLOR picker) has no reach into it, so without this a stale
  // ribbon picker would stay mounted over the fresh ByLayer drawing and could
  // still apply a colour to it. The swatch already subscribes to this store, so
  // reading one more slice adds NO new module edge.
  const resetEpoch = useCeColor((s) => s.resetEpoch);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchor, setAnchor] = useState<PickerAnchor | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fill = resolveSwatchFill(ceColor);
  const label = useColorLabel(ceColor);

  // Close the local picker whenever the document is reset / a new drawing is
  // started (resetShellState bumps `resetEpoch`). The initial-mount run is a
  // harmless no-op (`pickerOpen` is already false at mount).
  useEffect(() => {
    setPickerOpen(false);
  }, [resetEpoch]);

  // Compute the VIEWPORT anchor for the body-portal'd picker once it opens (the
  // button is already in the DOM). The picker is `position: fixed` (portal'd to
  // body to escape `.ribbon-shell{overflow:hidden}`), so the anchor is in
  // viewport coordinates from `getBoundingClientRect`. Default: open DOWNWARD
  // from the button bottom; FLIP upward when the popup would overflow the
  // viewport bottom (the ribbon is at the top so downward is the normal case —
  // the flip is a safety net so the picker is never cut off). The popup's own
  // measured height drives the flip once it has rendered.
  useLayoutEffect(() => {
    if (!pickerOpen) {
      setAnchor(null);
      return;
    }
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    // Estimated picker height until the popup measures itself (Index grid is the
    // tall case, ~360px); used only to choose the initial flip direction.
    const ESTIMATED_HEIGHT = 360;
    const openUpward =
      rect.bottom + ESTIMATED_HEIGHT > window.innerHeight &&
      rect.top - ESTIMATED_HEIGHT >= 0;
    setAnchor({
      left: rect.left,
      top: openUpward ? rect.top - ESTIMATED_HEIGHT : rect.bottom,
    });
  }, [pickerOpen]);

  function applyColor(value: string): void {
    const color = coerceColor(value);
    // Command-Engine one-truth: the invertible header/set-cecolor writes
    // doc.cecolor, and `editorStore.dispatch` mirrors that into the live
    // `cecolorStore` (the value the dispatch boundary reads for new-entity
    // stamping). No manual `setCeColor` — that would be a second truth.
    useEditorStore.getState().dispatch({
      type: "header/set-cecolor",
      source: "toolbar",
      color,
    });
    setPickerOpen(false);
    // CECOLOR round-9 FINDING A — if the dialog-less `-COLOR` keyword prompt is
    // armed (App `colorPromptPending`), picking a colour through THIS swatch
    // instead of typing a reply satisfies the prompt (the colour is applied), but
    // the swatch is a child component with no reach into App's pending flag, so
    // without this signal the next `LINE`/`CIRCLE`/… submission would be wrongly
    // swallowed as a colour reply. Bump the commit epoch App subscribes to so it
    // clears `colorPromptPending` (a store signal — zero new module edges). We do
    // NOT call the full reset path here: it must NOT re-close this swatch's own
    // picker mid-commit nor touch the command-line picker — only the pending flag
    // is moot. The bump is harmless when no prompt is armed (App clears an
    // already-false flag).
    useCeColorStore.getState().bumpColorCommitEpoch();
  }

  return (
    <div className="ribbon-cecolor-swatch" data-testid="ribbon-cecolor-swatch">
      <button
        ref={buttonRef}
        type="button"
        className="cecolor-swatch-button"
        data-testid="cecolor-swatch-button"
        title={`${t("cecolor.swatch.tooltip")}\n${t("cecolor.swatch.current", {
          value: label,
        })}`}
        onClick={() => {
          useEditorStore.getState().recordUiClick({
            target: "ribbon",
            controlId: "cecolor-swatch-button",
          });
          setPickerOpen((open) => !open);
        }}
      >
        <span
          className="cecolor-swatch-chip"
          aria-hidden="true"
          data-by-reference={fill === null ? "true" : "false"}
          style={fill !== null ? { backgroundColor: fill } : undefined}
        />
        <span className="cecolor-swatch-label">{label}</span>
      </button>
      {/* Portal the picker to `document.body` (fixed positioning) so it escapes
          the `.ribbon-shell{overflow:hidden}` clip; anchor it under the swatch
          button from the viewport-coordinate `getBoundingClientRect` snapshot. */}
      {pickerOpen && anchor
        ? createPortal(
            <AciColorPicker
              currentAci={ceColor.kind === "index" ? ceColor.aci : 256}
              initialTab={ceColor.kind === "rgb" ? "true" : "index"}
              initialHex={
                ceColor.kind === "rgb" ? colorToLegacyString(ceColor) : null
              }
              // CECOLOR round-6 FINDING D — the swatch is the advertised
              // current-colour control, so it exposes ByLayer / ByBlock (the
              // generic ACI tabs cannot). The selected button is highlighted
              // when the current CECOLOR is by-reference. The picker emits
              // "ByLayer"/"ByBlock"; `applyColor` → coerceColor → header/set-cecolor.
              showByReference
              currentReference={
                ceColor.kind === "byLayer"
                  ? "byLayer"
                  : ceColor.kind === "byBlock"
                    ? "byBlock"
                    : undefined
              }
              positionMode="fixed"
              anchorX={anchor.left}
              anchorY={anchor.top}
              onSelect={applyColor}
              onClose={() => setPickerOpen(false)}
              // round-5 FINDING D — let the picker's outside-mousedown detector
              // ignore the swatch toggle button so a SECOND click on it reliably
              // DISMISSES (without ignoreRef the outside-close + the button's
              // toggle cancel out and the picker never closes on re-click).
              ignoreRef={buttonRef}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
